import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Input, Select, Spin, type FormInstance } from "antd";
import { AimOutlined, ApartmentOutlined, CalendarOutlined, EditOutlined, PlusOutlined, UserOutlined } from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";
import ActionPlanDateTimePicker from "@/components/common/ActionPlan/Modals/ModalFormCreate/ActionPlanDateTimePicker";
import type { ActionPlanAiPendingPlan } from "@/types/action-plan-v2/ActionPlanAiPlan";
import {
  planToDraftFormValues,
  useActionPlanAiDraftScope,
  type MucTieuSelectOption,
} from "@/components/common/ActionPlanV2/actionPlanAiDraftScope";
import cardStyles from "@/components/common/AiChat/ActionPlanPendingCard.module.css";
import { useAuthStore } from "@/store/useAuthStore";

type Props = {
  plan: ActionPlanAiPendingPlan;
  form: FormInstance;
  disabled?: boolean;
  /** Gửi câu vào chat (vd. «Đổi người phụ trách…») — trợ lý hỏi lại, không chọn tự do trên thẻ. */
  onAsk?: (text: string) => void;
};

/** Mục tiêu đã chốt: hiển thị cố định (giá trị form vẫn giữ qua Form.Item). */
const MtLocked: React.FC<{ label: string; value?: string }> = ({ label }) => (
  <span className={cardStyles.factVal} title={label}>
    {label}
  </span>
);

export const ActionPlanAiDraftFields: React.FC<Props> = ({ plan, form, disabled, onAsk }) => {
  const isUpdate = plan.action === "update";
  const { mtOptions, loadingMt, profileId, scope } = useActionPlanAiDraftScope(plan);

  const planSyncKey = useMemo(
    () =>
      [
        plan.tenCongViec,
        plan.mucTieuId,
        plan.ngayBatDau,
        plan.ngayKetThuc,
        plan.moTa,
        plan.nguoiPhuTrachId,
      ].join("|"),
    [
      plan.tenCongViec,
      plan.mucTieuId,
      plan.ngayBatDau,
      plan.ngayKetThuc,
      plan.moTa,
      plan.nguoiPhuTrachId,
    ],
  );

  const syncedRef = useRef("");
  useEffect(() => {
    if (syncedRef.current === planSyncKey) return;
    syncedRef.current = planSyncKey;
    const values = planToDraftFormValues(plan);
    // Không tự chọn người phụ trách khi user giao được cho người khác — phải chọn trên thẻ
    if (!values.nguoiPhuTrachID && profileId && !plan.requireAssignee) {
      values.nguoiPhuTrachID = profileId;
    }
    form.setFieldsValue(values);
  }, [planSyncKey, form, profileId, plan]);

  useEffect(() => {
    const mt = String(plan.mucTieuId || "").trim();
    if (!mt || !mtOptions.length) return;
    if (!mtOptions.some((o) => o.value === mt)) return;
    if (form.getFieldValue("mtctid") === mt) return;
    form.setFieldValue("mtctid", mt);
  }, [plan.mucTieuId, mtOptions, form]);

  // Thẻ tạo việc CHƯA lưu: câu gửi nêu rõ «việc mới chưa lưu» + đủ thông tin để trợ lý dựng lại thẻ và hỏi người phụ trách
  // (không phải sửa việc đã có — tránh trợ lý gọi nhầm prepare_update rồi báo R08)
  const reassignText = () => {
    const ten = String(form.getFieldValue("tenCongViec") || plan.tenCongViec || "").trim();
    const range = form.getFieldValue("dateRange") as [Dayjs | null, Dayjs | null] | undefined;
    const fmt = (d?: Dayjs | null) => (d && dayjs.isDayjs(d) && d.isValid() ? d.format("DD/MM/YYYY") : "");
    const mtId = String(form.getFieldValue("mtctid") || plan.mucTieuId || "").trim();
    const mtTen = mtOptions.find((o) => o.value === mtId)?.label ?? plan.mucTieuTen;
    const parts = [
      fmt(range?.[0]) ? `bắt đầu ${fmt(range?.[0])}` : "",
      fmt(range?.[1]) ? `hạn ${fmt(range?.[1])}` : "",
      plan.parentId ? `việc con của «${plan.parentTen || plan.parentId}» (mã ${plan.parentId})` : mtId ? `mục tiêu «${mtTen ?? ""}» (mã ${mtId})` : "",
    ].filter(Boolean);
    return `Đổi người phụ trách cho việc mới chưa lưu «${ten}»${parts.length ? ` (${parts.join(", ")})` : ""} — hỏi tôi giao cho ai`;
  };

  const selfName = useAuthStore((st) => st.profile?.tenNhanVien?.trim() || "");
  const assigneeId = String(plan.nguoiPhuTrachId || (!plan.requireAssignee ? profileId : "") || "").trim();
  const assignee = useMemo(() => {
    if (!assigneeId) return null;
    const ten = (plan.nguoiPhuTrachTen || (assigneeId === profileId ? selfName || "Bạn" : assigneeId)).trim();
    const parts = ten.replace(/\(.*?\)/g, "").trim().split(/\s+/);
    const initials = ((parts.length > 1 ? parts[parts.length - 2][0] : "") + (parts[parts.length - 1]?.[0] || "?")).toUpperCase();
    return { ten, boPhan: plan.nguoiPhuTrachBoPhan || "", initials };
  }, [assigneeId, plan.nguoiPhuTrachBoPhan, plan.nguoiPhuTrachTen, profileId, selfName]);

  // Mục tiêu đã chốt (chọn trên thẻ / việc con theo cha) → giá trị cố định thay cho ô chọn
  const showMtChip = !isUpdate && Boolean(plan.mucTieuTen) && (Boolean(plan.lockMucTieu) || Boolean(plan.parentId));
  const [showMoTa, setShowMoTa] = useState(Boolean(String(plan.moTa ?? "").trim()));

  // Thẻ sửa: thay đổi thật «cũ → mới» (giá trị đang có do trợ lý gửi kèm)
  const changes = useMemo(() => {
    if (!isUpdate || !plan.before) return [];
    const fmt = (v?: string | null) => (v ? dayjs(String(v).slice(0, 10)).format("DD/MM/YYYY") : "—");
    const out: { label: string; from: string; to: string }[] = [];
    const b = plan.before;
    if ((b.tenCongViec || "") !== (plan.tenCongViec || "")) out.push({ label: "Tên", from: b.tenCongViec || "—", to: plan.tenCongViec || "—" });
    if ((b.ngayKetThuc || "").slice(0, 10) !== String(plan.ngayKetThuc || "").slice(0, 10))
      out.push({ label: "Hạn", from: fmt(b.ngayKetThuc), to: fmt(plan.ngayKetThuc) });
    if ((b.ngayBatDau || "").slice(0, 10) !== String(plan.ngayBatDau || "").slice(0, 10))
      out.push({ label: "Bắt đầu", from: fmt(b.ngayBatDau), to: fmt(plan.ngayBatDau) });
    if ((b.moTa || "") !== (plan.moTa || "")) out.push({ label: "Mô tả", from: b.moTa ? "có" : "—", to: plan.moTa ? "đã sửa" : "—" });
    return out;
  }, [isUpdate, plan.before, plan.moTa, plan.ngayBatDau, plan.ngayKetThuc, plan.tenCongViec]);

  return (
    <div className={cardStyles.quick}>
      {/* Dòng tiêu đề = tên việc, sửa tại chỗ */}
      <div className={cardStyles.titleRow}>
        <Form.Item
          name="tenCongViec"
          rules={[{ required: true, whitespace: true, message: "Nhập tên công việc" }]}
          className={cardStyles.titleItem}
        >
          <Input
            variant="borderless"
            placeholder="Nhập tên công việc"
            disabled={disabled}
            suffix={<EditOutlined className={cardStyles.titleEdit} aria-hidden />}
            className={cardStyles.titleInput}
            aria-label="Tên việc"
          />
        </Form.Item>
        {scope.deptLabel ? (
          <span className={cardStyles.scopeTag} title={`Phạm vi: ${scope.deptLabel}`}>
            {scope.deptLabel}
          </span>
        ) : null}
      </div>

      {changes.length ? (
        <ul className={cardStyles.changes} aria-label="Thay đổi">
          {changes.map((c) => (
            <li key={c.label}>
              <span className={cardStyles.changeKey}>{c.label}</span>
              <s>{c.from}</s>
              <span aria-hidden>→</span>
              <b>{c.to}</b>
            </li>
          ))}
        </ul>
      ) : null}

      <div className={cardStyles.facts}>
        {!isUpdate ? (
          // Người phụ trách do trợ lý hỏi đúng người + phòng ban (R03/R04) — không chọn tự do; «Đổi» hỏi lại qua chat
          <div className={cardStyles.fact}>
            <span className={cardStyles.factKey}>
              <UserOutlined aria-hidden /> Phụ trách
            </span>
            <span className={cardStyles.factVal}>
              <Form.Item name="nguoiPhuTrachID" hidden>
                <Input />
              </Form.Item>
              {assignee ? (
                <span className={cardStyles.assigneeInline}>
                  <span className={cardStyles.assigneeAvatar} aria-hidden>
                    {assignee.initials}
                  </span>
                  <span className={cardStyles.assigneeName}>{assignee.ten}</span>
                  {assignee.boPhan ? <span className={cardStyles.assigneeMeta}>· {assignee.boPhan}</span> : null}
                </span>
              ) : (
                <span className={cardStyles.assigneeMissing}>Chưa có người phụ trách</span>
              )}
            </span>
            {onAsk && !disabled ? (
              <Button
                type="link"
                size="small"
                className={cardStyles.factAction}
                onClick={() => onAsk(reassignText())}
              >
                {assignee ? "Đổi" : "Chọn"}
              </Button>
            ) : null}
          </div>
        ) : null}

        <Form.Item
          name="dateRange"
          label={
            <span className={cardStyles.factKey}>
              <CalendarOutlined aria-hidden /> Thời gian
            </span>
          }
          className={cardStyles.factItem}
          rules={[
            {
              validator: (_, val) => {
                const range = val as [unknown, unknown] | undefined;
                const start = dayjs.isDayjs(range?.[0]) ? range[0] : dayjs(range?.[0] as dayjs.ConfigType);
                const end = dayjs.isDayjs(range?.[1]) ? range[1] : dayjs(range?.[1] as dayjs.ConfigType);
                if (!start.isValid() || !end.isValid()) {
                  return Promise.reject(new Error("Chọn đủ ngày bắt đầu và kết thúc"));
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <ActionPlanDateTimePicker disabled={disabled} />
        </Form.Item>

        {!isUpdate ? (
          <Form.Item
            name="mtctid"
            label={
              <span className={cardStyles.factKey}>
                <AimOutlined aria-hidden /> Mục tiêu
              </span>
            }
            className={cardStyles.factItem}
            rules={plan.parentId ? [] : [{ required: true, message: "Chọn mục tiêu" }]}
          >
            {showMtChip ? (
              <MtLocked label={plan.mucTieuTen || ""} />
            ) : (
              <Select
                size="small"
                variant="borderless"
                showSearch
                optionFilterProp="label"
                loading={loadingMt}
                options={mtOptions as MucTieuSelectOption[]}
                placeholder="Chọn mục tiêu phụ trách"
                disabled={disabled || Boolean(plan.parentId) || Boolean(plan.lockMucTieu)}
                notFoundContent={loadingMt ? <Spin size="small" /> : "Không có mục tiêu"}
              />
            )}
          </Form.Item>
        ) : null}

        {!isUpdate && plan.parentId ? (
          <div className={cardStyles.fact}>
            <span className={cardStyles.factKey}>
              <ApartmentOutlined aria-hidden /> Việc cha
            </span>
            <span className={cardStyles.factVal} title={plan.parentTen || plan.parentId || undefined}>
              {plan.parentTen || plan.parentId}
            </span>
          </div>
        ) : null}
      </div>

      {showMoTa ? (
        <Form.Item name="moTa" className={cardStyles.moTa}>
          <Input.TextArea autoSize={{ minRows: 2, maxRows: 5 }} placeholder="Mô tả / kết quả mong đợi" disabled={disabled} />
        </Form.Item>
      ) : !disabled ? (
        <Button type="link" size="small" icon={<PlusOutlined />} className={cardStyles.addMoTa} onClick={() => setShowMoTa(true)}>
          Thêm mô tả
        </Button>
      ) : null}
    </div>
  );
};
